import React, { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import MenuBar from "../common/MenuPanel";
import FooterPanel from "../common/FooterPanel";
import LoadingPanel from "../common/LoadingPanel";
import { useAuth } from "../../auth/AuthContext";
import { axiosInstance } from "../../services/AdminServices";
import UtilMethods from "../../util/UtilMethods";
import * as XLSX from "xlsx";
import { REGIONS } from "../common/Constants";

import {
    FaEdit,
    FaTrash,
    FaSave,
    FaTimes,
    FaList,
    FaFileExcel,
    FaUpload,
    FaFileImport,
} from "react-icons/fa";

const ListSubscriptions = () => {
    const { user } = useAuth();

    const [subscriptions, setSubscriptions] = useState([]);
    const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
    const [editingSubscriptionId, setEditingSubscriptionId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // ✅ FIX: separate filter object and search text
    const [filter, setFilter] = useState({
        id: "",
        status: "",
        region: "",
        startDate: "",
        endDate: "",
        emailOrName: "",
    });

    const [searchText, setSearchText] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const subscriptionsPerPage = 15;
    const fileInputRef = React.useRef(null);

    /* ---------------- INITIAL LOAD ---------------- */

    const loadSubscriptions = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get("/subscription", {
                params: filter
            });

            setSubscriptions(response.data);
            setFilteredSubscriptions(response.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- EXPORT / IMPORT ---------------- */

    const handleExportToXLS = () => {
        const data = filteredSubscriptions.map((sub) => ({
            ID: sub.id,
            FirstName: sub.firstName,
            LastName: sub.lastName,
            Email: sub.email,
            Phone: sub.phone,
            Ethnicity: sub.ethnicity,
            Regions: Array.isArray(sub.regions) ? sub.regions.join(", ") : sub.regions,
            Language: sub.lang,
            Status: sub.status,
            CreatedAt: sub.createdAt,
        }));

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Subscriptions");
        XLSX.writeFile(workbook, "subscriptions_data.xlsx");
    };

    const handleImportFromXLS = (e) => {
        console.log("Importing subscriptions from XLS...");
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const wb = XLSX.read(evt.target.result, { type: "array" });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws);

                const imported = data.map((row) => ({
                    firstName: row.FirstName,
                    lastName: row.LastName,
                    email: row.Email,
                    phone: row.Phone,
                    ethnicity: row.Ethnicity,
                    regions: row.Regions ? row.Regions.split(", ").map(r => REGIONS[r.trim()] || r.trim()) : [],
                    lang: row.Language,
                    status: row.Status || "active",
                }));

                uploadSubscriptions(imported);
            } catch (err) {
                alert("Import failed: " + err.message);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const uploadSubscriptions = async (data) => {
        try {
            console.log("Uploading imported subscriptions...", data);
            await axiosInstance.post("/subscription/bulk", data);
            alert("Imported successfully");
            loadSubscriptions();
        } catch (err) {
            alert(err.message);
        }
    };

    /* ---------------- CRUD ---------------- */

    const handleEditClick = (id) => setEditingSubscriptionId(id);

    const handleCancelEdit = () => {
        setEditingSubscriptionId(null);
        loadSubscriptions();
    };

    const handleInputChange = (e, id) => {
        const { name, value } = e.target;
        setFilteredSubscriptions((prev) =>
            prev.map((sub) =>
                sub.id === id ? { ...sub, [name]: value } : sub
            )
        );
    };

    const handleUpdateSubscription = async (sub) => {
        try {
            console.log("Updating subscription...", sub);
            const response = await axiosInstance.put(`/subscription/${sub.id}`, sub);
            alert("Subscription updated successfully");
            setEditingSubscriptionId(null);
            loadSubscriptions();
        } catch (err) {
            console.error("Update failed:", err);
            alert("Update failed: " + err.message);
            setError(err.message);
        }
    };

    const handleDeleteSubscription = async (id) => {
        try {
            if (!window.confirm("Are you sure?")) return;
            await axiosInstance.delete(`/subscription/${id}`);
            alert("Subscription deleted successfully");
            loadSubscriptions();
        } catch (err) {
            console.error("Delete failed:", err);
            alert("Delete failed: " + err.message);
            setError(err.message);
        }
    };

    /* ---------------- FILTER ---------------- */

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilter(prev => ({ ...prev, [name]: value }));
    };

    const handleApplyFilter = () => {
        loadSubscriptions();
        setCurrentPage(1);
    };

    /* ---------------- SEARCH ---------------- */

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchText(value);

        const filtered = subscriptions.filter(
            (s) =>
                s.name?.toLowerCase().includes(value.toLowerCase()) ||
                s.email?.toLowerCase().includes(value.toLowerCase())
        );

        setFilteredSubscriptions(filtered);
        setCurrentPage(1);
    };

    /* ---------------- PAGINATION ---------------- */

    const indexOfLast = currentPage * subscriptionsPerPage;
    const indexOfFirst = indexOfLast - subscriptionsPerPage;
    const currentSubscriptions = filteredSubscriptions.slice(
        indexOfFirst,
        indexOfLast
    );

    const totalPages = Math.ceil(
        filteredSubscriptions.length / subscriptionsPerPage
    );

    /* ================= RENDER ================= */

    return (
        <>
            <MenuBar />

            {/* Banner */}
            <div className="container-fluid p-0">
                <div
                    className="banner-image_sm"
                    style={{
                        backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
                    }}
                >
                    <div className="banner-heading text-center">
                        <h1 className="text-white">Subscription Management</h1>
                    </div>
                </div>
            </div>

            <div className="container-fluid my-5">

                {/* ================= FILTER SECTION ================= */}
                <div className="mb-2">

                    {/* ---------- ROW 1 : FILTERS ---------- */}
                    <div className="row g-3">

                        <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                            <input
                                className="form-control form-control-sm"
                                placeholder="Filter by ID"
                                name="id"
                                value={filter.id}
                                onChange={handleFilterChange}
                            />
                        </div>

                        <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                            <input
                                className="form-control form-control-sm"
                                placeholder="Name or email"
                                value={searchText}
                                onChange={handleSearchChange}
                            />
                        </div>

                        <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                            <select
                                className="form-control form-control-sm"
                                name="status"
                                value={filter.status}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>

                        <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                            <select
                                className="form-control form-control-sm"
                                name="region"
                                value={filter.region}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Regions</option>
                                {REGIONS.map((r) => (
                                    <option key={r.value} value={r.value}>
                                        {r.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* ---------- ROW 2 : DATES + ACTIONS ---------- */}
                    <div className="row g-3 align-items-end mt-2">

                        <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                name="startDate"
                                value={filter.startDate}
                                onChange={handleFilterChange}
                            />
                        </div>

                        <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                name="endDate"
                                value={filter.endDate}
                                onChange={handleFilterChange}
                            />
                        </div>

                        {/* ACTION BUTTONS */}
                        <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                            <div className="d-flex flex-wrap gap-2 ">
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleApplyFilter}
                                >
                                    <FaList className="me-1" />
                                    List
                                </button>

                                <button
                                    className="btn btn-success btn-sm"
                                    onClick={handleExportToXLS}
                                >
                                    <FaFileExcel className="me-1" />
                                    Export
                                </button>

                                <button
                                    className="btn btn-info btn-sm"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <FaFileImport className="me-1" />
                                    Import
                                </button>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    hidden
                                    accept=".xlsx,.xls"
                                    onChange={handleImportFromXLS}
                                />
                            </div>
                        </div>

                    </div>
                </div>

                <div className="row g-3">

                    {/* ================= TABLE ================= */}
                    {loading ? (
                        <LoadingPanel />
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>First Name</th>
                                        <th>Last Name</th>
                                        <th>Email</th>
                                        <th>Regions</th>
                                        <th>Ethnicity</th>
                                        <th>Language</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {currentSubscriptions.map((sub) => (
                                        <tr key={sub.id}>
                                            <td>{sub.id}</td>

                                            {editingSubscriptionId === sub.id ? (
                                                <>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            name="firstName"
                                                            value={sub.firstName}
                                                            onChange={(e) => handleInputChange(e, sub.id)}
                                                            className="form-control form-control-sm"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            name="lastName"
                                                            value={sub.lastName}
                                                            onChange={(e) => handleInputChange(e, sub.id)}
                                                            className="form-control form-control-sm"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            value={sub.email}
                                                            onChange={(e) => handleInputChange(e, sub.id)}
                                                            className="form-control form-control-sm"
                                                        />
                                                    </td>

                                                    <td>
                                                        <select
                                                            name="regions"
                                                            multiple
                                                            value={Array.isArray(sub.regions) ? sub.regions : []}
                                                            onChange={(e) => {
                                                                const selected = Array.from(e.target.selectedOptions, option => option.value);
                                                                handleInputChange({ target: { name: 'regions', value: selected } }, sub.id);
                                                            }}
                                                            className="form-control form-control-sm"
                                                            style={{ minHeight: '80px' }}
                                                        >
                                                            {REGIONS.map((r) => (
                                                                <option key={r.value} value={r.value}>
                                                                    {r.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            name="ethnicity"
                                                            value={sub.ethnicity}
                                                            onChange={(e) => handleInputChange(e, sub.id)}
                                                            className="form-control form-control-sm"
                                                        />
                                                    </td>
                                                    <td>
                                                        <select
                                                            name="lang"
                                                            value={sub.lang}
                                                            onChange={(e) => handleInputChange(e, sub.id)}
                                                            className="form-control form-control-sm"
                                                        >
                                                            <option value="en">English</option>
                                                            <option value="fr">French</option>
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <select
                                                            name="status"
                                                            value={sub.status}
                                                            onChange={(e) => handleInputChange(e, sub.id)}
                                                            className="form-control form-control-sm"
                                                        >
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                        </select>
                                                    </td>
                                                    <td>{UtilMethods.formatDateTime(sub.createdAt)}</td>
                                                    <td className="d-flex gap-2 justify-content-center">
                                                        <button
                                                            className="btn btn-primary btn-sm"
                                                            onClick={() => handleUpdateSubscription(sub)}
                                                        >
                                                            <FaSave style={{ fontSize: "0.8rem" }} />
                                                        </button>
                                                        <button
                                                            className="btn btn-secondary btn-sm"
                                                            onClick={handleCancelEdit}
                                                        >
                                                            <FaTimes style={{ fontSize: "0.8rem" }} />
                                                        </button>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td>{sub.firstName}</td>
                                                    <td>{sub.lastName}</td>
                                                    <td>{sub.email}</td>
                                                    <td>{Array.isArray(sub.regions) ? sub.regions.join(", ") : sub.regions}</td>
                                                    <td>{sub.ethnicity}</td>
                                                    <td>{sub.lang}</td>
                                                    <td>{sub.status}</td>
                                                    <td>{UtilMethods.formatDateTime(sub.createdAt)}</td>
                                                    <td className="d-flex gap-2 justify-content-center">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                                                            <button
                                                                className="btn btn-warning btn-sm"
                                                                onClick={() => handleEditClick(sub.id)}
                                                            >
                                                                <FaEdit style={{ fontSize: "0.8rem" }} />
                                                            </button>
                                                        </OverlayTrigger>

                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                                                            <button
                                                                className="btn btn-danger btn-sm"
                                                                onClick={() => handleDeleteSubscription(sub.id)}
                                                            >
                                                                <FaTrash style={{ fontSize: "0.8rem" }} />
                                                            </button>
                                                        </OverlayTrigger>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>

                            </table>
                        </div>
                    )}

                    {/* ================= PAGINATION ================= */}
                    <div className="d-flex flex-wrap gap-2 justify-content-center mt-3">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                            <button
                                key={p}
                                className={`btn btn-sm ${p === currentPage ? "btn-primary" : "btn-light"
                                    }`}
                                onClick={() => setCurrentPage(p)}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                </div>
            </div >

            <FooterPanel />
        </>
    );

};

export default ListSubscriptions;
